import * as z from "zod/v4";
import { Currency, CurrencyMinorUnits } from "./currencies";

const moneySchema_ = z.object({
  amount: z.number().readonly(),
  currency: z.enum(Currency).readonly(),
});

export const MoneySchema: z.ZodType<Money> = moneySchema_;
export interface Money extends z.infer<typeof moneySchema_> {}

/**
 * Currency is invalid for this operation
 */
export class InvalidCurrencyError extends Error {
  override readonly name = "InvalidCurrencyError";

  constructor(
    message: string,
    readonly currency: Currency,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

/**
 * Add money
 */
export function moneyAdd(money1: Money, money2: Money): Money {
  if (money1.currency !== money2.currency) {
    throw new InvalidCurrencyError(
      `Currencies ${money1.currency} and ${money2.currency} do not match`,
      money2.currency,
    );
  }
  return { amount: money1.amount + money2.amount, currency: money1.currency };
}

/**
 * Subtract money
 */
export function moneySubtract(money1: Money, money2: Money): Money {
  if (money1.currency !== money2.currency) {
    throw new InvalidCurrencyError(
      `Currencies ${money1.currency} and ${money2.currency} do not match`,
      money2.currency,
    );
  }
  return { amount: money1.amount - money2.amount, currency: money1.currency };
}

/**
 * Multiply money
 */
export function moneyMultiply(money: Money, factor: number): Money {
  return { amount: money.amount * factor, currency: money.currency };
}

/**
 * Divide money
 */
export function moneyDivide(money: Money, divisor: number): Money {
  return { amount: money.amount / divisor, currency: money.currency };
}

/**
 * Create money from amount in minor unit
 */
export function moneyFromMinorAmount(
  minorAmount: number,
  currency: Currency,
): Money {
  return {
    amount: minorAmount / 10 ** (CurrencyMinorUnits[currency] ?? 0),
    currency,
  };
}

/**
 * Amount in minor unit
 */
export function moneyToMinorAmount(money: Money) {
  return money.amount * 10 ** (CurrencyMinorUnits[money.currency] ?? 0);
}

/**
 * Convert money to a string with the currency's minor unit precision.
 */
export function moneyToFixed(money: Money): string {
  return money.amount.toFixed(CurrencyMinorUnits[money.currency] ?? 0);
}
